import React, { useRef, useState, useCallback } from "react";
import { MoveHorizontal } from "lucide-react";
import { IMAGES } from "../../data/content";
import { Reveal, Eyebrow } from "./Reveal";

const BeforeAfter = ({ before, after, label, testid, tall }) => {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);
  const dragging = useRef(false);

  const setFromClientX = useCallback((clientX) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const p = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(2, Math.min(98, p)));
  }, []);

  const onDown = (e) => {
    dragging.current = true;
    setFromClientX(e.touches ? e.touches[0].clientX : e.clientX);
  };
  const onMove = (e) => {
    if (!dragging.current) return;
    setFromClientX(e.touches ? e.touches[0].clientX : e.clientX);
  };
  const onUp = () => { dragging.current = false; };

  return (
    <div
      ref={ref}
      data-testid={testid}
      className={`relative w-full ${tall ? "aspect-[3/4]" : "aspect-[4/3]"} rounded-3xl overflow-hidden border border-white/10 select-none cursor-ew-resize shadow-[0_20px_60px_rgba(0,0,0,0.5)]`}
      onMouseDown={onDown}
      onMouseMove={onMove}
      onMouseUp={onUp}
      onMouseLeave={onUp}
      onTouchStart={onDown}
      onTouchMove={onMove}
      onTouchEnd={onUp}
    >
      {/* After (base) */}
      <img src={after} alt="After transformation" className="absolute inset-0 h-full w-full object-cover pointer-events-none" draggable="false" />
      <span className="absolute top-4 right-4 z-20 rounded-full bg-[#53C653] px-3 py-1 text-xs font-bold text-[#111111] uppercase tracking-wide">After</span>

      {/* Before (clipped) */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ width: `${pos}%` }}>
        <img src={before} alt="Before transformation" className="absolute inset-0 h-full object-cover" style={{ width: `${100 / (pos / 100)}%`, maxWidth: "none" }} draggable="false" />
        <div className="absolute inset-0 bg-black/25" />
        <span className="absolute top-4 left-4 rounded-full bg-[#F56FB4] px-3 py-1 text-xs font-bold text-white uppercase tracking-wide">Before</span>
      </div>

      {/* Handle */}
      <div className="absolute top-0 bottom-0 z-20 w-0.5 bg-white/90" style={{ left: `${pos}%` }}>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white flex items-center justify-center shadow-lg">
          <MoveHorizontal size={20} className="text-[#111111]" />
        </div>
      </div>

      {label && (
        <div className="absolute bottom-4 left-4 z-20 rounded-xl bg-[#111111]/70 backdrop-blur-md border border-white/10 px-4 py-2">
          <p className="text-sm font-semibold text-white">{label}</p>
        </div>
      )}
    </div>
  );
};

export const Gallery = () => (
  <section id="gallery" className="relative py-24 md:py-32 bg-[#111111]">
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <div className="text-center mb-14">
        <Reveal><Eyebrow>Transformations</Eyebrow></Reveal>
        <Reveal delay={0.05}>
          <h2 className="font-heading font-extrabold uppercase text-white text-4xl md:text-5xl lg:text-6xl tracking-tight mt-3">
            Watch Properties <span className="text-[#F56FB4]">Bloom</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="text-white/60 max-w-xl mx-auto mt-4">Drag the slider to reveal the difference. Real spaces, reclaimed.</p>
        </Reveal>
      </div>

      <Reveal>
        <div className="grid lg:grid-cols-3 gap-6 items-start">
          <div className="lg:col-span-2">
            <BeforeAfter before={IMAGES.before} after={IMAGES.after} label="Full Garage Cleanout · Chester" testid="ba-slider-main" />
          </div>
          <div className="flex flex-col gap-6">
            <BeforeAfter before={IMAGES.before} after={IMAGES.after} label="Estate Cleanout · Media" testid="ba-slider-2" tall />
          </div>
        </div>
      </Reveal>

      <Reveal delay={0.1}>
        <div className="grid md:grid-cols-2 gap-6 mt-6">
          <BeforeAfter before={IMAGES.before} after={IMAGES.after} label="Basement Reclaim · Aston" testid="ba-slider-3" />
          <BeforeAfter before={IMAGES.after} after={IMAGES.before} label="Property Cleanout · Ridley" testid="ba-slider-4" />
        </div>
      </Reveal>
    </div>
  </section>
);

export default Gallery;
